/*
 * Biletul SSO legat de clientul care a pornit autentificarea (ca PKCE, RFC 7636).
 *
 * Fara legatura, biletul e o parola la purtator: cine il prinde - din bara de
 * adrese, dintr-un proxy, sau pe Android de pe schema proprie
 * `optimumdesk-catalog://auth`, pe care o poate declara orice alta aplicatie
 * instalata - il schimba pe o sesiune inaintea omului (audit IAM, 16 oct 2026).
 *
 * Cum: la apasarea butonului se face un secret aleator (`code_verifier`), se
 * tine in sessionStorage-ul acestei file, si pleaca spre portal numai amprenta
 * lui (`code_challenge` = base64url(SHA-256(verifier)), metoda S256). Portalul
 * pune amprenta in bilet. La schimb, aplicatia trimite secretul, iar api-ul
 * verifica sha256(secret) == amprenta din bilet. Cine a prins doar biletul n-are
 * secretul - acela n-a iesit niciodata din fila care a pornit autentificarea.
 *
 * Compatibil in ambele directii: un portal care nu stie de `code_challenge` il
 * ignora si emite biletul ca azi; un api care nu stie de `code_verifier` il
 * ignora la schimb. Legatura incepe sa conteze abia cand ambele il folosesc -
 * vezi descrierea schimbarii din ph4 in raportul livrarii.
 */

const STORAGE_KEY = 'catalog.sso.verifier';

/* Si in memorie: daca storage-ul e blocat, fila tot isi aminteste secretul pe
   drumul nativ (biletul vine prin adresa proprie, fara reincarcarea paginii). */
let inMemory: string | null = null;

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Adresa de pornire cu amprenta secretului in ea.
 *
 * Daca browserul n-are WebCrypto (context nesigur, browser foarte vechi), se
 * intoarce adresa neschimbata si nu se tine niciun secret: autentificarea merge
 * ca pana acum, nelegata, in loc sa nu mearga deloc.
 */
export async function bindStartUrl(startUrl: string): Promise<string> {
  const subtle = typeof crypto !== 'undefined' ? crypto.subtle : undefined;
  if (!subtle || typeof crypto.getRandomValues !== 'function') {
    forgetVerifier();
    return startUrl;
  }

  const raw = new Uint8Array(32);
  crypto.getRandomValues(raw);
  /* 43 de caractere base64url - in intervalul 43..128 cerut de RFC 7636. */
  const verifier = base64Url(raw);

  let challenge: string;
  try {
    const digest = await subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    challenge = base64Url(new Uint8Array(digest));
  } catch {
    forgetVerifier();
    return startUrl;
  }

  inMemory = verifier;
  try {
    storage()?.setItem(STORAGE_KEY, verifier);
  } catch {
    /* Ramane cel din memorie. */
  }

  const separator = startUrl.includes('?') ? '&' : '?';
  return (
    startUrl +
    separator +
    'code_challenge=' +
    encodeURIComponent(challenge) +
    '&code_challenge_method=S256'
  );
}

/**
 * Secretul pentru schimb, o singura data. Se sterge la citire: un al doilea
 * schimb cu acelasi secret n-are ce sa caute.
 */
export function takeVerifier(): string | null {
  let verifier = inMemory;
  inMemory = null;
  const store = storage();
  try {
    verifier = verifier ?? store?.getItem(STORAGE_KEY) ?? null;
    store?.removeItem(STORAGE_KEY);
  } catch {
    /* Ce era in memorie ajunge. */
  }
  return verifier;
}

function forgetVerifier(): void {
  inMemory = null;
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    /* Nimic de sters. */
  }
}
